import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { DrawerLink } from '../../app/DrawerLink';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { isDesktop } from '../../lib/viewport';
import { useChatDock } from '../../stores/chat';
import { useSession } from '../../stores/session';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { EmojiButton } from '../../components/ui/EmojiPicker';
import { SkeletonBlock, SkeletonLine } from '../../components/ui/Loader';
import { useEmojiComposer } from '../../lib/emoji';
import { subscribeToConversation } from '../../lib/realtime';
import { usePlayer } from '../../stores/player';
import { messages as dm, type Conversation, type Message } from './api';

/**
 * Messages (HANDOFF §6.5).
 *
 * ## Full-height, not a page in a column
 *
 * Every other screen here is a centred column that scrolls. Messaging is the
 * one that is not: a conversation has a fixed header, a scrolling middle, and a
 * composer pinned to the bottom, and the list beside it scrolls independently.
 * Putting that inside a page that also scrolls gives you two scrollbars and a
 * composer that wanders off the screen.
 *
 * So this fills exactly the space between the top bar and the dock, and nothing
 * on it scrolls except the two panes that are supposed to.
 *
 * ## Two details in the arithmetic
 *
 * `-mb-6` cancels the 1.5rem of clearance `AppShell` puts under every page. That
 * clearance exists so the last item of a scrolling page is not hidden behind
 * the dock — a real problem, and the right default. Here the composer is
 * already pinned above the dock, so the gap would just be a strip of empty
 * background, and the panel is meant to meet the bar.
 *
 * `dvh` rather than `vh`: on mobile Safari `vh` is the *largest* viewport, so a
 * page sized in it hides its own bottom edge behind the browser chrome — which
 * here would be the composer, the one thing that must never be off-screen.
 */
/*
 * The full height of the screen between the header and the dock.
 *
 * `--spacing-chrome-top` rather than `4rem`, because the header carries a
 * border and a screen sized to its content alone overflows by that pixel - see
 * the token. `-mb-6` cancels the shell's bottom padding.
 */
const FULL_HEIGHT = 'h-[calc(100dvh-var(--spacing-chrome-top)-var(--spacing-dock))] -mb-6';

export function MessagesPage() {
  const { id } = useParams();
  const conversationId = id ? Number(id) : null;

  const { data, isPending } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => dm.inbox(),
    // A backstop: `message.new` on an open thread refetches this too, and the
    // header's envelope polls independently.
    refetchInterval: 120_000,
  });

  const conversations = data?.items ?? [];

  return (
    <div className={`flex ${FULL_HEIGHT} border-t border-line`}>
      {/* ----------------------------------------------------------------- */}
      {/* The list                                                           */}
      {/* ----------------------------------------------------------------- */}
      <aside
        className={`w-full shrink-0 flex-col border-line md:flex md:w-80 md:border-r lg:w-96 ${
          conversationId === null ? 'flex' : 'hidden'
        }`}
        data-conversation-list
      >
        <header className="flex h-14 shrink-0 items-center px-4">
          <h1 className="text-base font-semibold text-ink">Messages</h1>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {isPending ? (
            <div className="flex flex-col gap-1 px-1 py-2" aria-busy="true">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-2">
                  <SkeletonBlock className="h-10 w-10 shrink-0 rounded-[var(--radius-pill)]" />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <SkeletonLine width="55%" />
                    <SkeletonLine width="80%" />
                  </div>
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <p className="px-2 py-4 text-sm text-soft">
              Nothing here yet. Open somebody's profile and press Message to start one.
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5" data-conversations>
              {conversations.map((conversation) => (
                <li key={conversation.id}>
                  <InboxRow conversation={conversation} active={conversation.id === conversationId} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* ----------------------------------------------------------------- */}
      {/* The thread                                                         */}
      {/* ----------------------------------------------------------------- */}
      <section
        className={`min-w-0 flex-1 flex-col ${conversationId === null ? 'hidden md:flex' : 'flex'}`}
      >
        {conversationId === null ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-soft">Pick a conversation.</p>
          </div>
        ) : (
          <Thread conversationId={conversationId} />
        )}
      </section>
    </div>
  );
}

function InboxRow({ conversation, active }: { conversation: Conversation; active: boolean }) {
  const name =
    conversation.other?.display_name || conversation.other?.username || conversation.title || 'Conversation';

  return (
    <Link
      to={`/messages/${conversation.id}`}
      data-conversation={conversation.id}
      className={`flex items-center gap-3 rounded-[var(--radius-control)] px-2 py-2.5 transition-colors ${
        active ? 'bg-raised' : 'hover:bg-raised'
      }`}
    >
      <Avatar src={conversation.other?.avatar ?? null} name={name} size={40} />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{name}</span>
          {conversation.last_message_at && (
            <span className="meta-caps shrink-0">{ago(conversation.last_message_at)}</span>
          )}
        </div>

        <div className="mt-0.5 flex items-center gap-2">
          <span
            className={`min-w-0 flex-1 truncate text-xs ${
              conversation.unread > 0 ? 'font-medium text-soft' : 'text-faint'
            }`}
          >
            {conversation.preview || 'No messages yet'}
          </span>

          {conversation.unread > 0 && (
            <span
              className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-accent px-1 text-[10px] font-semibold text-accent-ink"
              data-unread={conversation.unread}
            >
              {conversation.unread > 9 ? '9+' : conversation.unread}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function Thread({ conversationId }: { conversationId: number }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const bottom = useRef<HTMLDivElement>(null);

  // Inserts at the caret and hands focus back - see `useEmojiComposer`.
  const emoji = useEmojiComposer(body, setBody);

  const { data, isPending } = useQuery({
    queryKey: ['thread', conversationId],
    queryFn: () => dm.thread(conversationId),
  });

  const send = useMutation({
    mutationFn: (text: string) => dm.send(conversationId, { body: text }),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['thread', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  const markRead = useMutation({
    mutationFn: () => dm.markRead(conversationId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['conversations'] }),
  });

  /*
   * The socket says a message arrived; this asks what it was.
   *
   * §8.2's rule - the event carries an id and a sender and nothing else,
   * because `mine` and an attached track's visibility both depend on who is
   * reading. Rendering from the payload would mean the socket had to know that.
   */
  useEffect(
    () =>
      subscribeToConversation(conversationId, () => {
        void queryClient.invalidateQueries({ queryKey: ['thread', conversationId] });
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }),
    [conversationId, queryClient],
  );

  const items = data?.items ?? [];

  // Opening a thread reads it. Unlike the notification bell — where a glance is
  // not the same as having dealt with it — reading a conversation *is* the act.
  useEffect(() => {
    if (items.length > 0) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, items.length]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [items.length]);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (body.trim() === '') return;

    send.mutate(body.trim());
  };

  const other = data?.conversation.participants[0];
  const name = other?.display_name || other?.username || 'Conversation';

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line px-3 sm:px-4">
        {/* Only where the list is not on screen. On a wide window it is right
            there and a back button would go nowhere useful. */}
        <Link
          to="/messages"
          aria-label="Back to messages"
          className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-ink md:hidden"
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <path d="M10 3L5 8l5 5" />
          </svg>
        </Link>

        {other && (
          <>
            <Avatar src={null} name={name} size={32} />
            <Link to={`/${other.username}`} className="min-w-0 leading-tight">
              <span className="block truncate text-sm font-medium text-ink hover:underline">{name}</span>
              <span className="meta-caps block truncate">@{other.username}</span>
            </Link>
          </>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-6" data-thread>
        {isPending ? (
          /* Alternating sides, so the skeleton has the shape of a conversation
             rather than of a list. */
          <div className="flex flex-col gap-3" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={i % 2 === 0 ? 'self-start' : 'self-end'}>
                <SkeletonBlock
                  className="h-10 rounded-[var(--radius-card)]"
                  style={{ width: i % 2 === 0 ? '14rem' : '10rem' }}
                />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-soft">No messages yet. Say something.</p>
        ) : (
          /*
            No width cap on the list.

            It was `mx-auto max-w-3xl`, which floated the whole conversation in
            a centred column inside a much wider pane - so "my" messages aligned
            to the middle of the screen rather than to the edge of the thread.
            The bubbles carry their own cap below, which is what keeps a line of
            text readable; the container does not need one too.
          */
          <ul className="flex flex-col gap-2">
            {items.map((message, index) => (
              <li key={message.id} data-message={message.id}>
                <Bubble
                  message={message}
                  // A run of messages from the same person reads as one block:
                  // the timestamp goes on the last of them, not on each.
                  showTime={items[index + 1]?.mine !== message.mine || index === items.length - 1}
                />
              </li>
            ))}
          </ul>
        )}
        <div ref={bottom} />
      </div>

      <form
        onSubmit={submit}
        className="flex shrink-0 items-center gap-2 border-t border-line px-3 py-3 sm:px-6"
        data-message-form
      >
        <input
          ref={emoji.ref as React.RefObject<HTMLInputElement>}
          className="min-w-0 flex-1 rounded-[var(--radius-pill)] border border-line bg-app px-4 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong"
          placeholder={other ? `Message ${other.display_name || other.username}` : 'Write a message…'}
          value={body}
          maxLength={4000}
          data-message-body
          disabled={send.isPending}
          onChange={(event) => setBody(event.target.value)}
        />
        <EmojiButton onSelect={emoji.insert} />
        <Button type="submit" loading={send.isPending} disabled={body.trim() === ''}>
          Send
        </Button>
      </form>
    </>
  );
}

function Bubble({ message, showTime }: { message: Message; showTime: boolean }) {
  const play = usePlayer((state) => state.play);

  if (message.deleted) {
    return (
      <div className={`flex ${message.mine ? 'justify-end' : 'justify-start'}`}>
        <p className="px-1 text-xs italic text-faint">This message was deleted.</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${message.mine ? 'items-end' : 'items-start'}`}>
      <div
        className={`max-w-[min(32rem,80%)] px-3.5 py-2.5 ${
          message.mine
            ? 'rounded-[var(--radius-card)] rounded-br-sm bg-accent text-accent-ink'
            : 'rounded-[var(--radius-card)] rounded-bl-sm bg-raised text-ink'
        }`}
      >
        {message.body && (
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{message.body}</p>
        )}

        {/*
          A shared track, playable in place.
          §6.5 calls this the single highest-intent discovery path — a friend
          sending you a song converts better than any algorithm — so it renders
          as something you press, not a link to go and find.
        */}
        {message.track && (
          <div
            className={`mt-2 flex items-center gap-2.5 rounded-[var(--radius-control)] p-2 ${
              message.mine ? 'bg-black/20' : 'bg-app'
            }`}
            data-shared-track={message.track.id}
          >
            <button
              type="button"
              aria-label={`Play ${message.track.title}`}
              onClick={() => void play(message.track!, [message.track!], 'feed')}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink transition-colors hover:bg-accent-hover"
            >
              <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 translate-x-px" fill="currentColor" aria-hidden="true">
                <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
              </svg>
            </button>

            {message.track.artwork_small && (
              <img
                src={message.track.artwork_small}
                alt=""
                loading="lazy"
                className="h-9 w-9 shrink-0 rounded-[var(--radius-control)] object-cover"
              />
            )}

            <DrawerLink to={`/tracks/${message.track.id}`} className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-xs font-medium">{message.track.title}</span>
              {message.track.owner && (
                <span className="block truncate text-[11px] opacity-70">
                  @{message.track.owner.username}
                </span>
              )}
            </DrawerLink>
          </div>
        )}
      </div>

      {showTime && (
        <span className="meta-caps mt-1 px-1">{clock(message.created_at)}</span>
      )}
    </div>
  );
}

/** `14:32` — the time a message was sent, in the reader's own locale. */
function clock(iso: string): string {
  return new Date(iso.replace(' ', 'T') + 'Z').toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `3m`, `4h`, `2d`, then a date. Terse, because it sits in a narrow column. */
function ago(iso: string): string {
  const then = new Date(iso.replace(' ', 'T') + 'Z');
  const minutes = Math.max(0, Math.round((Date.now() - then.getTime()) / 60000));

  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h`;
  if (minutes < 60 * 24 * 7) return `${Math.round(minutes / 1440)}d`;

  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * The "Message" button on a profile.
 *
 * Starts the DM and docks it. Find-or-create on the server, so pressing it
 * twice — or at the same moment as the other person — lands on one thread.
 *
 * ## Why it no longer navigates
 *
 * It used to go to `/messages/{id}`, which takes somebody off the profile they
 * were reading in order to type one line. The dock holds the conversation
 * without moving them (§9.2), and it outlives the page they are on, so the
 * reply arrives wherever they end up.
 *
 * Below 768px there is no dock and the route is still the answer — see
 * `DockChat` for why a 320px window inside a 360px viewport is not one.
 */
export function MessageButton({ username }: { username: string }) {
  const navigate = useNavigate();
  const dock = useChatDock((state) => state.start);
  const me = useSession((state) => state.user);

  const client = useQueryClient();

  const start = useMutation({
    mutationFn: () => dm.start(username),
    onSuccess: (conversation) => {
      /*
       * The inbox is told to refresh, but the dock is not made to wait for it:
       * `start` is handed the correspondent so the tab is named on the first
       * frame. A conversation with no messages is not in the inbox list at all,
       * so waiting would mean a tab labelled "Conversation" until somebody
       * spoke.
       */
      void client.invalidateQueries({ queryKey: ['conversations'] });

      if (isDesktop()) {
        dock(conversation.id, conversation.other);

        return;
      }

      navigate(`/messages/${conversation.id}`);
    },
  });

  /*
   * Nothing to offer a signed-out visitor, matching `FollowButton`.
   *
   * The button rendered for everybody until an e2e run caught it: a logged-out
   * page still showed "Message", and pressing it posted a conversation that
   * came back 401 and did nothing visible. A control that looks available and
   * silently fails is worse than one that is not there.
   */
  if (!me) {
    return null;
  }

  return (
    <Button variant="secondary" loading={start.isPending} onClick={() => start.mutate()}>
      Message
    </Button>
  );
}
